import { Component, EventEmitter, OnInit } from '@angular/core';
import { PermissionService } from '../permission.service';
import { Permission } from '../permission';
import { NotificationService } from '../../shared/notification.service';

@Component({
  selector: 'app-permission-list',
  templateUrl: 'list.component.html',
  styleUrls: [],
})
export class PermissionListComponent implements OnInit {
  public permission: Permission;

  /**
   * A list of all the permissions currently loaded.
   *
   * @type {Permission[]}
   */
  public permissions: Permission[] = [];

  /**
   * Are we currently performing a loading action?
   *
   * @type {boolean}
   */
  public isLoading: boolean = true;

  /**
   * The total number of pages in this list of permissions.
   *
   * @type {number}
   */
  public totalPages: number = 1;

  /**
   * The current page of permissions that we are viewing.
   *
   * @type {number}
   */
  public currentPage: number = 1;

  /**
   * The total number of permissions that can be viewed in the system.
   *
   * @type {number}
   */
  public totalResults: number = 0;

  /**
   * An Emitter to alert other components when a permission has been deleted.
   *
   * @type {EventEmitter<boolean>}
   */
  public permissionDeleted: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * Load up all our required services and subscribe to every observable we need.
   *
   * @param {PermissionService} permissionService
   * @param {NotificationService} notificationService
   */
  constructor(
    private permissionService: PermissionService,
    private notificationService: NotificationService,
  ) {
    this.permissionService.loading.subscribe(isLoading => {
      this.isLoading = isLoading;
    });
    this.permissionService.totalPages.subscribe(totalPages => {
      this.totalPages = totalPages;
    });
    this.permissionService.currentPage.subscribe(currentPage => {
      this.currentPage = currentPage;
    });
    this.permissionService.totalResults.subscribe(totalResults => {
      this.totalResults = totalResults;
    });
  }

  /**
   * When the component is initialised we set it to load
   * page one of the permission list.
   */
  public ngOnInit(): void {
    this.loadPermissions(1);
  }

  /**
   * When we receive a page change from the pagination
   * component we load the appropriate page for the
   * permission list.
   *
   * @param {number} pageNumber
   */
  public onPageChanged(pageNumber: number): void {
    this.loadPermissions(pageNumber);
  }

  /**
   * Loads a list of permissions using the given per page limit.
   *
   * @param {number} page The current page we require.
   * @param {number} limit The number of results per page.
   */
  public loadPermissions(page?: number, limit?: number): void {
    const listPermissions = this.permissionService
      .listPermissions(page, limit)
      .subscribe((permissions: Permission[]) => {
        this.permissions = permissions;
        listPermissions.unsubscribe();
      });
  }

  /**
   * When receiving confirmation of an item being deleted
   * we call the back end with the appropriate permission ID
   * to delete the permission.
   *
   * @param {string} id
   */
  public delete(id: string) {
    this.permissionService.deletePermission(id).subscribe(
      deleted => {
        this.notificationService.success(
          `Permission Deleted`,
          `Permission with ID ${id} was deleted.`,
          5,
        );
        this.permissionDeleted.next(true);
        this.onPageChanged(this.currentPage);
      },
      err => {
        this.notificationService.error(
          `Permission Not Deleted`,
          err.error.message,
          10,
        );
        this.permissionDeleted.next(false);
        this.onPageChanged(this.currentPage);
      },
    );
  }
}
